import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useAuth, RedirectToSignIn, SignIn, SignUp } from "@clerk/react";
import "./App.css";

// Eager imports — layout & auth infrastructure
import TenantProvider from "./contexts/TenantContext";
import { CampaignDataProvider } from "./contexts/CampaignDataContext";
import PageTransition from "./components/common/PageTransition";
import SidebarLayout from "./components/layout/SidebarLayout";
import SettingsLayout from "./components/layout/SettingsLayout";
import { AuthTokenSync } from "./components/AuthTokenSync";
import { CurrentUserProvider, usePageAccess } from "./contexts/CurrentUserContext";
import { EventTrackerProvider } from "./components/EventTrackerProvider";
import { PaywallProvider } from "./components/PaywallModal";
import { PlanFeatureGate } from "./components/PlanFeatureGate";
import { AppErrorBoundary } from "./components/common/AppErrorBoundary";
import { TooltipProvider } from "./components/ui/tooltip";

// Lazy-loaded components — code-split per route
const CampaignManagement = React.lazy(() => import("./pages/CampaignManagement"));
const CreateCampaign = React.lazy(() => import("./pages/CreateCampaign"));
const CampaignRedirectWrapper = React.lazy(() => import("./components/CampaignRedirectWrapper"));
const LegacyCampaignRedirect = React.lazy(() => import("./components/LegacyCampaignRedirect"));
const SecureDashboard = React.lazy(() => import("./components/SecureDashboard"));
const SchedulerCalendar = React.lazy(() => import("./pages/SchedulerCalendar").then(m => ({ default: m.SchedulerCalendar })));
const DeveloperDash = React.lazy(() => import("./pages/DeveloperDash"));
const QuickBooksAdmin = React.lazy(() => import("./pages/QuickBooksAdmin"));
const FeatureFlags = React.lazy(() => import("./pages/FeatureFlags"));
const InvoicePay = React.lazy(() => import("./pages/InvoicePay"));
const CampaignMessaging = React.lazy(() => import("./pages/CampaignMessaging"));
const ActiveCampaigns = React.lazy(() => import("./pages/ActiveCampaigns"));
const AccountManagement = React.lazy(() => import("./pages/AccountManagement"));
const DisplaySettings = React.lazy(() => import("./pages/DisplaySettings"));
const MediaVault = React.lazy(() => import("./pages/MediaVault"));
const Documents = React.lazy(() => import("./pages/Documents"));
const Prospects = React.lazy(() => import("./pages/Prospects"));
const ConstructConnectCRM = React.lazy(() => import("./pages/ConstructConnectCRM"));
const ConstructConnectLeadDetail = React.lazy(() => import("./pages/ConstructConnectLeadDetail"));
const GeneratedContent = React.lazy(() => import("./pages/GeneratedContent"));
const CampaignInfo = React.lazy(() => import("./pages/CampaignInfo"));
const Support = React.lazy(() => import("./pages/Support"));
const Conversations = React.lazy(() => import("./pages/Conversations"));
const UsersManagement = React.lazy(() => import("./pages/UsersManagement"));
const InviteUsers = React.lazy(() => import("./pages/InviteUsers"));
const AdsDashboard = React.lazy(() => import("./pages/AdsDashboard").then(m => ({ default: m.AdsDashboard })));
const AdsRoundDetail = React.lazy(() => import("./pages/AdsDashboard/AdsRoundDetail").then(m => ({ default: m.AdsRoundDetail })));
const GoToMarket = React.lazy(() => import("./pages/GoToMarket"));
const SkillDetail = React.lazy(() => import("./pages/SkillDetail"));
const SettingsPlan = React.lazy(() => import("./pages/SettingsPlan"));
const SettingsInvoices = React.lazy(() => import("./pages/SettingsInvoices"));
const SettingsIntegrations = React.lazy(() => import("./pages/SettingsIntegrations"));
const EventMonitor = React.lazy(() => import("./pages/EventMonitor"));
const AdPerformance = React.lazy(() => import("./pages/AdPerformance"));
const SocialListening = React.lazy(() => import("./pages/SocialListening"));
const AdvertiserVerification = React.lazy(() => import("./pages/AdvertiserVerification"));
const Chat = React.lazy(() => import("./pages/Chat"));
const Onboarding = React.lazy(() => import("./pages/Onboarding"));

function RequireAuth({
  children
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return <></>;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return <>{children}</>;
}

// Hard-gate logic — three layers:
//
// 1. `plan_selection` / `awaiting_payment` → forced to /onboarding. Auth pages
//    are exempt to avoid redirect loops.
// 2. `campaign_creation` → forced to /t/:slug/campaigns/new. The user has
//    paid but hasn't created their first campaign yet, so we don't let them
//    poke around the rest of the app until they do. The target page itself
//    plus the auth/onboarding paths are exempt.
// 3. `complete` → no gate.
//
// Admins always compute as `complete` (they're never gated).
// New users land directly in the app on the Free plan (auto-provisioned by
// the auth middleware). The hard pre-payment gate is gone — payment is now
// triggered by individual premium-feature gates (Phase B/C/D), not by
// blocking signup. This component intentionally is a passthrough for now;
// keep it wrapping routes so we can re-introduce a gate later if product
// changes its mind.
function RequireOnboarded({ children }: { children: React.ReactNode }): React.ReactElement {
  return <>{children}</>;
}

function RequirePage({
  pageKey,
  children,
  fallback
}: {
  pageKey: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): React.ReactElement {
  const { canAccessPage, loading } = usePageAccess();
  const [searchParams] = useSearchParams();

  if (loading) {
    return <></>;
  }

  if (!canAccessPage(pageKey)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    const campaign = searchParams.get("campaign");
    const redirectTo = campaign
      ? `/dashboard?campaign=${campaign}`
      : "/dashboard";
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

function RequireAnyPage({
  pageKeys,
  children,
  fallback
}: {
  pageKeys: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): React.ReactElement {
  const { canAccessPage, loading } = usePageAccess();
  const [searchParams] = useSearchParams();

  if (loading) {
    return <></>;
  }

  if (!pageKeys.some((pageKey) => canAccessPage(pageKey))) {
    if (fallback) {
      return <>{fallback}</>;
    }
    const campaign = searchParams.get("campaign");
    const redirectTo = campaign
      ? `/dashboard?campaign=${campaign}`
      : "/dashboard";
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }): React.ReactElement {
  const { role, loading } = usePageAccess();

  if (loading) {
    return <></>;
  }

  if (role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Used when a customer can't see the campaign-management page (or has no
 * campaigns yet). Every signup gets a tenant on the Free plan, so the
 * "no active tenancy" branch should never fire in normal flow — but if it
 * does, we still send the user somewhere useful instead of a dead-end message.
 *
 * - Has tenant + campaigns → /t/:slug (the campaign list)
 * - Has tenant + no campaigns → /t/:slug/campaigns/new
 * - No tenant → /t/:slug/campaigns/new on whatever slug we have, or a
 *   thin spinner while /api/me catches up (auth middleware provisioning
 *   may still be in flight on the very first request after signup).
 */
function NoTenancyFallbackComponent(): React.ReactElement {
  const { tenantSlug, loading } = usePageAccess();

  if (loading) {
    return <></>;
  }

  if (tenantSlug) {
    return <Navigate to={`/t/${tenantSlug}`} replace />;
  }

  // No tenant slug yet — most commonly the first request after signup
  // before /api/me has refetched. Spin briefly; the polling cycle in
  // useCurrentUser will pick up the new state.
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#fff",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
          Setting up your workspace…
        </p>
      </div>
    </div>
  );
}

const NoTenancyFallback = <NoTenancyFallbackComponent />;

function RequireCampaigns({ children }: { children: React.ReactNode }): React.ReactElement {
  const { hasCampaigns, tenantSlug, loading } = usePageAccess();

  if (loading) {
    return <></>;
  }

  if (!hasCampaigns) {
    if (tenantSlug) {
      return <Navigate to={`/t/${tenantSlug}`} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function DefaultRedirect(): React.ReactElement {
  const { tenantSlug, loading } = usePageAccess();

  if (loading) {
    return <></>;
  }

  if (tenantSlug) {
    return <Navigate to={`/t/${tenantSlug}`} replace />;
  }

  return <Navigate to="/dashboard" replace />;
}

function AppContent(): React.ReactElement {
  const location = useLocation();
  const [tenantIdMap] = React.useState<Record<string, string>>({});

  if (location.pathname.startsWith("/public/")) {
    return (
      <AppErrorBoundary>
        <div className="App">
          <TooltipProvider delayDuration={200}>
            <AnimatePresence mode="wait">
              <Routes location={location}>
                <Route
                  path="/public/social-listening/:campaignKey"
                  element={<PageTransition><SocialListening publicMode /></PageTransition>}
                />
                <Route
                  path="/public/advertiser-verification/:campaignKey"
                  element={<PageTransition><AdvertiserVerification publicMode /></PageTransition>}
                />
              </Routes>
            </AnimatePresence>
          </TooltipProvider>
        </div>
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
      <div className="App">
        <AuthTokenSync>
          {/* EventTrackerProvider must be inside AuthTokenSync so the JWT token
              is available before api.ingestEvents() is called. */}
          {/* TODO: tenantIdMap is currently always empty — slug-to-UUID resolution
              happens via location.pathname matching but never resolves to a real
              tenant UUID. Tenant resolution should be wired from TenantContext or
              a dedicated lookup once the tracker needs tenant_id on events. */}
          <EventTrackerProvider tenantIdMap={tenantIdMap} />
          <CurrentUserProvider>
            <TooltipProvider delayDuration={200}>
            <PaywallProvider>
            <div className="app-content">
              <AnimatePresence mode="wait">
                <Routes location={location}>
                {/* Auth */}
                <Route
                  path="/login"
                  element={
                    <SignIn
                      routing="path"
                      path="/login"
                      fallbackRedirectUrl="/"
                      signUpUrl="/sign-up"
                    />
                  }
                />
                <Route
                  path="/sign-up"
                  element={
                    <div className="flex min-h-screen items-center justify-center bg-background p-6">
                      <SignUp
                        routing="path"
                        path="/sign-up"
                        signInUrl="/login"
                        fallbackRedirectUrl="/onboarding"
                      />
                    </div>
                  }
                />

                {/* Onboarding (gated only by Clerk auth — must not be wrapped in
                    RequireOnboarded or it would redirect-loop). */}
                <Route
                  path="/onboarding"
                  element={
                    <RequireAuth>
                      <PageTransition><Onboarding /></PageTransition>
                    </RequireAuth>
                  }
                />

                {/* Public payment page — no auth, validated by random token. */}
                <Route
                  path="/pay/:token"
                  element={<PageTransition><InvoicePay /></PageTransition>}
                />

                {/* Public campaign pages - no Clerk auth, tenant slug, or tenant id. */}
                <Route
                  path="/public/social-listening/:campaignKey"
                  element={<PageTransition><SocialListening publicMode /></PageTransition>}
                />
                <Route
                  path="/public/advertiser-verification/:campaignKey"
                  element={<PageTransition><AdvertiserVerification publicMode /></PageTransition>}
                />

                {/* Default redirect */}
                <Route path="/" element={<RequireAuth><RequireOnboarded><DefaultRedirect /></RequireOnboarded></RequireAuth>} />

                {/* All routes below share one persistent SidebarLayout so the
                    sidebar never unmounts/remounts during navigation. The
                    RequireOnboarded gate forces unfinished users back to
                    /onboarding. */}
                <Route element={<RequireOnboarded><SidebarLayout /></RequireOnboarded>}>
                  {/* Tenant-scoped routes (Clerk-protected) */}
                  <Route path="/t/:tenantSlug" element={<RequireAuth><TenantProvider /></RequireAuth>}>
                    <Route element={<CampaignDataProvider />}>
                      <Route index element={<RequirePage pageKey="campaign-management" fallback={NoTenancyFallback}><PageTransition><CampaignManagement /></PageTransition></RequirePage>} />
                      <Route path="dashboard" element={<PageTransition><CampaignRedirectWrapper segment="dashboard" fallback={<SecureDashboard />} /></PageTransition>} />
                      <Route path="dashboard/:campaignKey" element={<PageTransition><SecureDashboard /></PageTransition>} />
                      <Route path="prospects" element={<PageTransition><CampaignRedirectWrapper segment="prospects" fallback={<Prospects />} /></PageTransition>} />
                      <Route path="prospects/:campaignKey" element={<PageTransition><Prospects /></PageTransition>} />
                      <Route path="construct-connect-crm" element={<PageTransition><CampaignRedirectWrapper segment="construct-connect-crm" fallback={<ConstructConnectCRM />} /></PageTransition>} />
                      <Route path="construct-connect-crm/:campaignKey" element={<PageTransition><ConstructConnectCRM /></PageTransition>} />
                      <Route path="construct-connect-crm/:campaignKey/lead/:leadId" element={<PageTransition><ConstructConnectLeadDetail /></PageTransition>} />
                      <Route path="conversations" element={<PageTransition><CampaignRedirectWrapper segment="conversations" fallback={<Conversations />} /></PageTransition>} />
                      <Route path="conversations/:campaignKey" element={<PageTransition><Conversations /></PageTransition>} />
                      <Route path="content" element={<PageTransition><CampaignRedirectWrapper segment="content" fallback={<GeneratedContent />} /></PageTransition>} />
                       <Route path="content/:campaignKey" element={<PageTransition><GeneratedContent /></PageTransition>} />
                      <Route path="campaign-info" element={<PageTransition><CampaignRedirectWrapper segment="campaign-info" fallback={<CampaignInfo />} /></PageTransition>} />
                      <Route path="campaign-info/:campaignKey" element={<PageTransition><CampaignInfo /></PageTransition>} />
                      <Route path="ad-performance" element={<PageTransition><CampaignRedirectWrapper segment="ad-performance" fallback={<AdPerformance />} /></PageTransition>} />
                      <Route path="ad-performance/:campaignKey" element={<PageTransition><AdPerformance /></PageTransition>} />
                      <Route path="social-listening" element={<RequirePage pageKey="social-listening"><PageTransition><CampaignRedirectWrapper segment="social-listening" fallback={<SocialListening />} /></PageTransition></RequirePage>} />
                      <Route path="social-listening/:campaignKey" element={<RequirePage pageKey="social-listening"><PageTransition><SocialListening /></PageTransition></RequirePage>} />
                      <Route path="advertiser-verification" element={<RequirePage pageKey="advertiser-verification"><PageTransition><CampaignRedirectWrapper segment="advertiser-verification" fallback={<AdvertiserVerification />} /></PageTransition></RequirePage>} />
                      <Route path="advertiser-verification/:campaignKey" element={<RequirePage pageKey="advertiser-verification"><PageTransition><AdvertiserVerification /></PageTransition></RequirePage>} />
                      <Route
                        path="campaigns/new"
                        element={
                          <RequirePage pageKey="campaign-management" fallback={NoTenancyFallback}>
                            <PlanFeatureGate
                              feature="create_campaign"
                              reason="Creating a campaign is included in any paid plan. Pick one to continue."
                            >
                              <PageTransition><CreateCampaign /></PageTransition>
                            </PlanFeatureGate>
                          </RequirePage>
                        }
                      />
                    </Route>
                    <Route path="documents" element={<RequirePage pageKey="documents"><PageTransition><Documents /></PageTransition></RequirePage>} />

                    {/* Settings routes — no PageTransition: sub-pages share a
                        persistent SettingsLayout (nav mounts once, only the
                        inner content swaps via Outlet) */}
                    <Route path="settings" element={<RequirePage pageKey="settings"><SettingsLayout /></RequirePage>}>
                      <Route path="account" element={<AccountManagement />} />
                      <Route path="display" element={<DisplaySettings />} />
                      <Route path="plan" element={<SettingsPlan />} />
                      <Route path="invoices" element={<SettingsInvoices />} />
                      <Route path="integrations" element={<SettingsIntegrations />} />
                      <Route path="invite-users" element={<RequirePage pageKey="invite-users"><InviteUsers /></RequirePage>} />
                      <Route path="support" element={<Support />} />
                      <Route path="advertiser-verification" element={<RequirePage pageKey="advertiser-verification"><CampaignRedirectWrapper segment="settings/advertiser-verification" fallback={<AdvertiserVerification />} /></RequirePage>} />
                      <Route path="advertiser-verification/:campaignKey" element={<RequirePage pageKey="advertiser-verification"><AdvertiserVerification /></RequirePage>} />
                    </Route>
                  </Route>

                  {/* Global admin routes (Clerk + RBAC protected) */}
                  <Route path="/admin" element={<Navigate to="/active-campaigns" replace />} />
                  <Route path="/scheduler" element={<RequireAuth><RequirePage pageKey="scheduler"><PageTransition><SchedulerCalendar /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/developer-dash" element={<RequireAuth><RequirePage pageKey="developer-dash"><PageTransition><DeveloperDash /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/admin/quickbooks" element={<RequireAuth><RequirePage pageKey="admin"><PageTransition><QuickBooksAdmin /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/admin/feature-flags" element={<RequireAuth><RequireAdmin><PageTransition><FeatureFlags /></PageTransition></RequireAdmin></RequireAuth>} />
                  <Route path="/campaign-messaging" element={<RequireAuth><RequirePage pageKey="campaign-messaging"><PageTransition><CampaignMessaging /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/active-campaigns" element={<RequireAuth><RequirePage pageKey="active-campaigns"><PageTransition><ActiveCampaigns /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/users" element={<RequireAuth><RequirePage pageKey="users"><PageTransition><UsersManagement /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/invite-users" element={<RequireAuth><Navigate to="/settings/invite-users" replace /></RequireAuth>} />

                  {/* Standalone routes */}
                  <Route path="/settings" element={<RequireAuth><SettingsLayout /></RequireAuth>}>
                    <Route index element={<PageTransition><AccountManagement /></PageTransition>} />
                    <Route path="account" element={<PageTransition><AccountManagement /></PageTransition>} />
                    <Route path="display" element={<PageTransition><DisplaySettings /></PageTransition>} />
                    <Route path="plan" element={<PageTransition><SettingsPlan /></PageTransition>} />
                    <Route path="invoices" element={<PageTransition><SettingsInvoices /></PageTransition>} />
                    <Route path="integrations" element={<PageTransition><SettingsIntegrations /></PageTransition>} />
                    <Route path="invite-users" element={<RequirePage pageKey="invite-users"><PageTransition><InviteUsers /></PageTransition></RequirePage>} />
                    <Route path="support" element={<PageTransition><Support /></PageTransition>} />
                    <Route path="advertiser-verification/:campaignKey" element={<RequirePage pageKey="advertiser-verification"><PageTransition><AdvertiserVerification /></PageTransition></RequirePage>} />
                  </Route>
                  <Route path="/media-vault" element={<RequireAuth><RequireCampaigns><PageTransition><MediaVault /></PageTransition></RequireCampaigns></RequireAuth>} />
                  <Route path="/support" element={<RequireAuth><PageTransition><Support /></PageTransition></RequireAuth>} />
                  <Route path="/ads" element={<RequireAuth><RequirePage pageKey="ads"><PageTransition><AdsDashboard /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/ads/round/:runId" element={<RequireAuth><RequirePage pageKey="ads"><PageTransition><AdsRoundDetail /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/prowork" element={<RequireAuth><RequireAdmin><PageTransition><Chat /></PageTransition></RequireAdmin></RequireAuth>} />
                  <Route path="/connectors" element={<RequireAuth><RequirePage pageKey="go-to-market"><PageTransition><GoToMarket /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/connectors/skills/:skillSlug" element={<RequireAuth><RequirePage pageKey="go-to-market"><PageTransition><SkillDetail /></PageTransition></RequirePage></RequireAuth>} />
                  <Route path="/event-monitor" element={<RequireAuth><RequirePage pageKey="event-monitor"><PageTransition><EventMonitor /></PageTransition></RequirePage></RequireAuth>} />

                  {/* Legacy URL redirects — old ?campaign= style routes */}
                  <Route path="/dashboard" element={<RequireAuth><LegacyCampaignRedirect fallback={<CampaignDataProvider><PageTransition><SecureDashboard /></PageTransition></CampaignDataProvider>} /></RequireAuth>} />
                  <Route path="/prospects" element={<RequireAuth><RequireCampaigns><LegacyCampaignRedirect fallback={<CampaignDataProvider><PageTransition><Prospects /></PageTransition></CampaignDataProvider>} /></RequireCampaigns></RequireAuth>} />
                  <Route path="/construct-connect-crm" element={<RequireAuth><RequireCampaigns><RequireAnyPage pageKeys={["construct-connect-crm", "prospects"]}><LegacyCampaignRedirect fallback={<PageTransition><ConstructConnectCRM /></PageTransition>} /></RequireAnyPage></RequireCampaigns></RequireAuth>} />
                  <Route path="/conversations" element={<RequireAuth><RequireCampaigns><LegacyCampaignRedirect fallback={<CampaignDataProvider><PageTransition><Conversations /></PageTransition></CampaignDataProvider>} /></RequireCampaigns></RequireAuth>} />
                  <Route path="/generated-content" element={<RequireAuth><RequireCampaigns><LegacyCampaignRedirect fallback={<PageTransition><GeneratedContent /></PageTransition>} /></RequireCampaigns></RequireAuth>} />
                  <Route path="/ad-performance" element={<RequireAuth><RequireCampaigns><LegacyCampaignRedirect fallback={<PageTransition><AdPerformance /></PageTransition>} /></RequireCampaigns></RequireAuth>} />
                  <Route path="/social-listening" element={<RequireAuth><RequireCampaigns><RequirePage pageKey="social-listening"><LegacyCampaignRedirect fallback={<PageTransition><SocialListening /></PageTransition>} /></RequirePage></RequireCampaigns></RequireAuth>} />
                  <Route path="/advertiser-verification" element={<RequireAuth><RequireCampaigns><RequirePage pageKey="advertiser-verification"><LegacyCampaignRedirect fallback={<PageTransition><AdvertiserVerification /></PageTransition>} /></RequirePage></RequireCampaigns></RequireAuth>} />
                </Route>
              </Routes>
            </AnimatePresence>
          </div>
            </PaywallProvider>
            </TooltipProvider>
        </CurrentUserProvider>
      </AuthTokenSync>
    </div>
    </AppErrorBoundary>
  );
}

function App(): React.ReactElement {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
